import { useApolloClient } from "@apollo/client";
import { LabeledValue, LabeledValueProps } from "@msys/ui";
import { Box, Button, Chip, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useCategories } from "./useCategories.js";

interface Props {
  categoryIds: string[];
  label?: string;
  heading?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightButton?: React.ReactNode;
  required?: boolean;
  addParentsWhenNotIncluded?: boolean;
  removeChildrenIfIncluded?: boolean;
  excludeDeletedCategories?: boolean;
  labelProps?: LabeledValueProps["labelProps"];
}

const LIMIT = 5;

export const CategoriesLabeledValue = ({
  categoryIds,
  label,
  heading,
  leftIcon,
  rightButton,
  required = false,
  addParentsWhenNotIncluded = true,
  removeChildrenIfIncluded = true,
  excludeDeletedCategories = true,
  labelProps,
}: Props) => {
  const { t } = useTranslate(["Categories", "Global"]);
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          minWidth: 0,
          flex: 1,
        }}
      >
        <CategoriesChips
          categoryIds={categoryIds}
          label={label ?? t("Categories", { ns: "Categories" })}
          heading={heading}
          required={required}
          removeChildrenIfIncluded={removeChildrenIfIncluded}
          addParentsWhenNotIncluded={addParentsWhenNotIncluded}
          excludeDeletedCategories={excludeDeletedCategories}
          labelProps={labelProps}
        />
        {leftIcon}
      </Stack>
      {rightButton ? (
        <Box
          sx={{
            display: "flex",
          }}
        >
          {rightButton}
        </Box>
      ) : null}
    </Stack>
  );
};

export function CategoriesChips({
  categoryIds,
  label,
  heading,
  required,
  addParentsWhenNotIncluded,
  removeChildrenIfIncluded,
  excludeDeletedCategories,
  labelProps,
}: {
  categoryIds: string[];
  label?: string;
  heading?: React.ReactNode;
  required?: boolean;
  addParentsWhenNotIncluded?: boolean;
  removeChildrenIfIncluded?: boolean;
  excludeDeletedCategories?: boolean;
  labelProps?: LabeledValueProps["labelProps"];
}) {
  const { t } = useTranslate(["Categories", "Global"]);

  const [showMore, setShowMore] = React.useState(false);
  const { resolveCategories, loading } = useCategories();

  const categories =
    categoryIds.length > 0
      ? resolveCategories(categoryIds, {
          addParentsWhenNotIncluded,
          removeChildrenIfIncluded,
          excludeDeletedCategories,
        })
      : [];

  const chips = loading ? (
    ""
  ) : categories.length > 0 ? (
    <Stack
      direction="row"
      spacing={0.5}
      useFlexGap
      sx={{
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {(showMore || categories.length <= LIMIT
        ? categories
        : categories.slice(0, LIMIT - 1)
      ).map(category => (
        <Chip
          key={category.id}
          label={category.name}
          size="small"
          variant="outlined"
          color="primary"
        />
      ))}
      {categories.length > LIMIT && (
        <>
          {!showMore && (
            <span>
              …
              {t("and {count} more", {
                ns: "Global",
                count: categories.length - (LIMIT - 1),
              })}
            </span>
          )}
          <Button
            onClick={() => setShowMore(!showMore)}
            variant="text"
            color="secondary"
            size="extra-small"
          >
            {showMore
              ? t("Show less", { ns: "Global" })
              : t("Show more", { ns: "Global" })}
          </Button>
        </>
      )}
    </Stack>
  ) : (
    t("Not set", { ns: "Global" })
  );

  if (heading) {
    return (
      <Stack spacing={0.5}>
        {heading}
        {chips}
      </Stack>
    );
  }

  return label ? (
    <LabeledValue
      label={label + (required ? " *" : "")}
      notSet={!categoryIds.length}
      labelProps={labelProps}
    >
      {chips}
    </LabeledValue>
  ) : (
    chips
  );
}
